// "Your sign-in" and the sign-in page's second step (decision 20, QA-093): the words for each way to
// give a second factor, the deadline banner, and the QR code an authenticator app scans.

import type { TwoFactorMethod } from '@flowology/client';
import { toQR } from 'toqr';

export const TWO_FACTOR_LABEL: Record<
  TwoFactorMethod,
  { title: string; lede: string; field: string; switchTo: string; hint: string }
> = {
  app: {
    title: 'Two-step sign-in',
    lede: 'Enter the 6-digit code from your authenticator app.',
    field: '6-digit code',
    switchTo: 'Use the authenticator app',
    hint: 'The app’s code is 6 digits.',
  },
  sms: {
    title: 'Two-step sign-in',
    lede: 'We text a 6-digit code to your verified mobile.',
    field: '6-digit code',
    switchTo: 'Text me a code instead',
    hint: 'The texted code is 6 digits.',
  },
  recovery: {
    title: 'Use a recovery code',
    lede: 'One of the 10 codes you saved when you turned on two-factor. Each one works once.',
    field: 'Recovery code',
    switchTo: 'Use a recovery code',
    hint: 'A recovery code looks like abcde-fghij.',
  },
};

/** The way to offer first: the app if they have it, then a text, then a recovery code. */
export function firstMethod(methods: readonly TwoFactorMethod[]): TwoFactorMethod {
  return (['app', 'sms', 'recovery'] as const).find((m) => methods.includes(m)) ?? 'recovery';
}

/** A quick shape check before asking the server (it decides). */
export function codeLooksRight(method: TwoFactorMethod, code: string): boolean {
  const c = code.replace(/[\s-]+/g, '');
  return method === 'recovery' ? /^[A-Za-z0-9]{8,16}$/.test(c) : /^\d{6}$/.test(c);
}

export interface TwoFactorState {
  on: boolean;
  app: boolean;
  recoveryLeft: number;
  required: boolean;
  deadline: string | null;
  blocked: boolean;
  appOnly: boolean;
  resetHere?: boolean;
  /** Reset by the owner while not required of them (QA-183): asked once, and they may skip it. */
  resetPrompt?: { at: string; by: string; message: string } | null;
}

/**
 * The banner for someone the company requires two-factor of, in the sign-in prototype's words: the
 * days left of the 7, then, past them, that only setup is open.
 */
export function deadlineBanner(
  s: Pick<TwoFactorState, 'on' | 'required' | 'deadline' | 'blocked'>,
  now = new Date(),
): { tone: 'warn' | 'bad'; text: string; detail: string } | null {
  if (s.on || !s.required) return null;
  if (s.blocked)
    return {
      tone: 'bad',
      text: 'Finish setting up two-factor to continue.',
      detail: s.deadline ? 'The 7 days to turn it on have passed.' : 'Your company requires it.',
    };
  const detail = 'Your company asks you to add a second step when you sign in.';
  if (!s.deadline) return { tone: 'warn', text: 'Set up two-factor within 7 days.', detail };
  const left = Date.parse(s.deadline) - now.getTime();
  // Under a day, the hours (package 3, 22 Oct: "1 day left" showed with under an hour to go, then
  // everything locked), and urgent; under an hour, that.
  if (left < 86_400_000) {
    const hours = Math.floor(left / 3_600_000);
    return {
      tone: 'bad',
      text: `Set up two-factor now: ${hours < 1 ? 'less than an hour' : `${hours} ${hours === 1 ? 'hour' : 'hours'}`} left before only setup opens.`,
      detail,
    };
  }
  const days = Math.ceil(left / 86_400_000);
  // Warn while there is time, as the prototype does: the days left carry the urgency.
  return {
    tone: 'warn',
    text: `Set up two-factor within 7 days: ${days} ${days === 1 ? 'day' : 'days'} left.`,
    detail,
  };
}

/** The QR code for an otpauth:// link as one SVG path, a module per unit with a 4-module quiet zone. */
export function qrPath(text: string): { size: number; d: string } {
  const m = toQR(text);
  const n = Math.round(Math.sqrt(m.length));
  let d = '';
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (m[y * n + x]) d += `M${x + 4} ${y + 4}h1v1h-1z`;
  return { size: n + 8, d };
}

/** The secret as an app's manual entry shows it: groups of four. */
export const groupedSecret = (secret: string) => secret.replace(/(.{4})/g, '$1 ').trim();

/**
 * After signing in with a recovery code, on Your sign-in (which shows how many are left): that each
 * works once, and to make new ones when they run low.
 */
export function recoveryNote(left: number): string {
  if (left === 0)
    return 'That was your last recovery code. Make new ones now, so you can still get in if you lose your phone.';
  return left <= 2
    ? 'You signed in with a recovery code. Each one works once: make new ones before they run out.'
    : 'You signed in with a recovery code. Each one works once.';
}
